import { useState, useEffect } from 'react';
import { X, Mail, ArrowRight, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsletterPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('newsletter_dismissed');
    const subscribed = localStorage.getItem('newsletter_subscribed');
    if (dismissed || subscribed) {
      setIsDismissed(true);
      return;
    }

    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent >= 50 && !isDismissed) {
        setIsVisible(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDismissed]);

  const handleClose = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('newsletter_dismissed', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email, name });
      if (res.data.already_subscribed) {
        toast.info('You are already subscribed!');
      } else {
        toast.success('Subscribed! Check your inbox soon.');
      }
      setIsSubscribed(true);
      localStorage.setItem('newsletter_subscribed', 'true');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible || isDismissed) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4" data-testid="newsletter-popup">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />
      <div className="relative bg-white w-full max-w-lg overflow-hidden" style={{ boxShadow: '0 24px 48px rgba(10,15,26,0.15)' }}>
        {!isSubscribed ? (
          <>
            <div className="p-8 pb-0">
              <button
                onClick={handleClose}
                data-testid="newsletter-close"
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center hover:bg-[#F1F5F9] transition-colors"
                style={{ color: '#94A3B8' }}
              >
                <X size={18} />
              </button>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: '#FFF4E6' }}>
                  <BookOpen size={24} style={{ color: '#FF5722' }} />
                </div>
                <div>
                  <h3
                    className="text-lg font-bold"
                    style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
                  >
                    Free Stock Market Guide
                  </h3>
                  <p className="text-xs" style={{ color: '#94A3B8' }}>
                    For Indian investors
                  </p>
                </div>
              </div>

              <p className="text-sm leading-relaxed mb-2" style={{ color: '#475569' }}>
                Get our <strong style={{ color: '#0A0F1A' }}>free guide</strong> on building wealth through the Indian stock market. Plus, receive weekly market insights, trading tips, and investment strategies directly in your inbox.
              </p>

              <div className="flex flex-wrap gap-3 mb-6">
                {['Weekly market analysis', 'Trading strategies', 'IPO alerts', 'SIP recommendations'].map((item) => (
                  <span
                    key={item}
                    className="text-xs px-2 py-1"
                    style={{ backgroundColor: '#F1F5F9', color: '#475569' }}
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="px-8 pb-8">
              <div className="space-y-3">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  data-testid="newsletter-name"
                  className="w-full px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722] text-sm"
                  style={{ color: '#0A0F1A', backgroundColor: '#FAFAFA' }}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address *"
                  required
                  data-testid="newsletter-email"
                  className="w-full px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722] text-sm"
                  style={{ color: '#0A0F1A', backgroundColor: '#FAFAFA' }}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="newsletter-submit"
                  className="w-full py-3 font-semibold text-sm flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: isSubmitting ? '#94A3B8' : '#FF5722',
                    color: 'white',
                    border: 'none',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isSubmitting ? 'Subscribing...' : (
                    <>
                      Get Free Guide
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-center mt-3" style={{ color: '#94A3B8' }}>
                No spam. Unsubscribe anytime.
              </p>
            </form>
          </>
        ) : (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#ECFDF5' }}>
              <Mail size={28} style={{ color: '#10B981' }} />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}>
              You're in!
            </h3>
            <p className="text-sm mb-6" style={{ color: '#475569' }}>
              Your free stock market guide is on its way. Keep an eye on your inbox for weekly market insights.
            </p>
            <button
              onClick={handleClose}
              data-testid="newsletter-done"
              className="px-6 py-2 font-semibold text-sm"
              style={{ backgroundColor: '#0A0F1A', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              Continue Reading
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterPopup;
